import { useState } from "react";
import {
  Check,
  Copy,
  KeyRound,
  MoreVertical,
  RefreshCw,
  UserPlus,
  Users,
} from "lucide-react";
import { AxiosError } from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useMe } from "@/api/hooks/useAuth";
import {
  useCreateUser,
  useUpdateUser,
  useUsers,
  type AppUser,
  type Rola,
} from "@/api/hooks/useUsers";
import {
  MIN_DLZKA_HESLA,
  skopirujDoSchranky,
  vygenerujHeslo,
} from "@/lib/heslo";

/**
 * Správa účtov zamestnancov — len pre administrátora.
 *
 * Doteraz sa účty zakladali z CLI na serveri, čo znamená SSH pri každom
 * novom človeku.
 *
 * Sekcia sa vykreslí vždy, aj keď je autentifikácia na serveri vypnutá —
 * vtedy backend vráti 409 a my ukážeme, čo treba prepnúť. Skryť ju by
 * znamenalo, že sa admin nikdy nedozvie, prečo login nefunguje.
 */
const ROLY: { kod: Rola; nazov: string }[] = [
  { kod: "rozpoctar", nazov: "Rozpočtár" },
  { kod: "obchodnik", nazov: "Obchodník" },
];

/** Čo rola smie (KOLO 231) — tie isté pravidlá počíta server. */
const POPIS_ROLY: Record<Rola, string> = {
  rozpoctar:
    "Nábytok, výroba a jej cenník; tovar, koeficient a zľavu ponuky len vidí.",
  obchodnik:
    "Tovar, cenník tovaru, koeficient a zľava ponuky; v nábytku mení len cenu.",
};

export function PouzivateliaSection(): JSX.Element {
  const { data: meData } = useMe();
  const me = meData?.data;
  const authVypnuta = me?.auth_enabled === false;
  const jeAdmin = me?.user?.je_admin === true;

  const query = useUsers(!authVypnuta && jeAdmin);
  const users = query.data?.data ?? [];

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <Users className="h-5 w-5 text-primary" />
          Používatelia
        </h2>
        <p className="text-xs text-muted-foreground">
          Účty zamestnancov. Meno prihláseného sa zapisuje do histórie zmien
          ako autor.
        </p>
      </div>

      {authVypnuta ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <p className="font-semibold">
            Prihlasovanie je na serveri vypnuté.
          </p>
          <p className="mt-1">
            Kým je vypnuté, každý sa hlási menom z prehliadača a účty sa
            nedajú spravovať. Zapnutie je krok na serveri: v{" "}
            <code className="font-mono">/opt/configurator/.env</code> nastav{" "}
            <code className="font-mono">ENV=production</code> a{" "}
            <code className="font-mono">AUTH_ENABLED=true</code>, potom založ
            prvý admin účet príkazom{" "}
            <code className="font-mono">
              docker compose exec backend python -m
              tandem_budget.scripts.create_user create --username … --admin
            </code>
            .
          </p>
        </div>
      ) : !jeAdmin ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Správa účtov je len pre administrátora.
        </p>
      ) : (
        <>
          <NovyUcetForm />
          {query.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prihlasovacie meno</TableHead>
                    <TableHead>Meno</TableHead>
                    <TableHead className="w-[150px]">Rola</TableHead>
                    <TableHead className="w-[110px]">Stav</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      jaSam={u.username === me?.user?.username}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Pole s heslom + generovanie + kopírovanie.
 *
 * Heslo je **viditeľné**, nie `type="password"`: admin ho zakladá niekomu
 * inému a musí mu ho vedieť odovzdať. Server ho už nikdy nevráti — ukladá
 * sa len pbkdf2 hash — takže hviezdičky by chránili pred nikým a bránili
 * jedinému človeku, ktorý ho potrebuje prečítať.
 */
function HesloPole({
  id,
  label,
  hodnota,
  onZmena,
}: {
  id: string;
  label: string;
  hodnota: string;
  onZmena: (h: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <div className="flex items-center gap-1">
        <Input
          id={id}
          // Zámerne `text`, nie `password` — pozri docstring
          type="text"
          value={hodnota}
          onChange={(e) => onZmena(e.target.value)}
          className="h-9 font-mono"
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onZmena(vygenerujHeslo())}
          title="Vygenerovať náhodné heslo"
          aria-label="Vygenerovať heslo"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
        <KopirovatButton text={hodnota} />
      </div>
      <p className="text-[10px] text-muted-foreground">
        Min. {MIN_DLZKA_HESLA} znakov. Vygenerované heslo je bez zameniteľných
        znakov (0/O, 1/l), aby sa dalo prepísať z papiera.
      </p>
    </div>
  );
}

function KopirovatButton({ text }: { text: string }): JSX.Element {
  const [hotovo, setHotovo] = useState(false);

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={!text}
      onClick={() => {
        void skopirujDoSchranky(text).then((ok) => {
          if (!ok) {
            toast({
              variant: "destructive",
              title: "Nepodarilo sa skopírovať",
              description: "Označ heslo myšou a skopíruj ručne.",
            });
            return;
          }
          setHotovo(true);
          window.setTimeout(() => setHotovo(false), 2000);
        });
      }}
      title="Skopírovať heslo do schránky"
      aria-label="Skopírovať heslo"
    >
      {hotovo ? (
        <Check className="h-4 w-4 text-emerald-600" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );
}

function NovyUcetForm(): JSX.Element {
  // Formulár je zbalený — účty sa zakladajú raz za čas, hlavná vec na
  // stránke je zoznam
  const [otvoreny, setOtvoreny] = useState(false);
  const [username, setUsername] = useState("");
  const [meno, setMeno] = useState("");
  const [heslo, setHeslo] = useState("");
  const [jeAdmin, setJeAdmin] = useState(false);
  const [rola, setRola] = useState<Rola>("rozpoctar");
  // Po vytvorení sa formulár vyprázdni, takže heslo by zmizlo skôr, než ho
  // stihne odovzdať — preto zostáva v potvrdzovacom boxe
  const [vytvoreny, setVytvoreny] = useState<{
    username: string;
    heslo: string;
  } | null>(null);
  const create = useCreateUser();

  const mozeUlozit =
    username.trim().length >= 3 &&
    meno.trim().length > 0 &&
    heslo.length >= MIN_DLZKA_HESLA;

  async function uloz(): Promise<void> {
    const meno_uctu = username.trim();
    try {
      await create.mutateAsync({
        username: meno_uctu,
        meno: meno.trim(),
        heslo,
        je_admin: jeAdmin,
        rola,
      });
      setVytvoreny({ username: meno_uctu, heslo });
      setUsername("");
      setMeno("");
      setHeslo("");
      setJeAdmin(false);
      setRola("rozpoctar");
      setOtvoreny(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Účet sa nevytvoril",
        description: extractErrorMessage(e as AxiosError),
      });
    }
  }

  return (
    <div className="space-y-3">
      {vytvoreny ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-800 dark:bg-emerald-950">
          <div>
            <p className="font-semibold">
              Účet {vytvoreny.username} je vytvorený.
            </p>
            <p className="text-xs text-muted-foreground">
              Odovzdaj kolegovi heslo — už sa nikde nezobrazí.
            </p>
          </div>
          <code className="rounded bg-background px-2 py-1 font-mono text-sm">
            {vytvoreny.heslo}
          </code>
          <KopirovatButton text={vytvoreny.heslo} />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setVytvoreny(null)}
            className="ml-auto"
          >
            Rozumiem
          </Button>
        </div>
      ) : null}

      {otvoreny ? (
        <div className="space-y-4 rounded-md border bg-background-alt/40 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="u-username" className="text-xs">
                  Prihlasovacie meno
                </Label>
                <Input
                  id="u-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  placeholder="jozko"
                  className="h-9 font-mono"
                />
                <p className="text-[10px] text-muted-foreground">
                  Malé písmená bez diakritiky, min. 3 znaky.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="u-meno" className="text-xs">
                  Meno do histórie
                </Label>
                <Input
                  id="u-meno"
                  value={meno}
                  onChange={(e) => setMeno(e.target.value)}
                  placeholder="Jozef Mrkvička"
                  className="h-9"
                />
                <p className="text-[10px] text-muted-foreground">
                  Takto sa človek podpíše pod každú zmenu.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <HesloPole
                id="u-heslo"
                label="Heslo"
                hodnota={heslo}
                onZmena={setHeslo}
              />
              <label className="flex items-start gap-2 rounded-md border bg-background p-2 text-sm">
                <input
                  type="checkbox"
                  checked={jeAdmin}
                  onChange={(e) => setJeAdmin(e.target.checked)}
                  className="mt-0.5 h-4 w-4"
                />
                <span>
                  Administrátor
                  <span className="block text-[10px] text-muted-foreground">
                    Vidí odpracovaný čas, prihlásenia v histórii a spravuje
                    účty.
                  </span>
                </span>
              </label>
              {/* Rola (KOLO 231) — admin má všetky práva, rola ho neobmedzí */}
              <label className="flex items-center gap-2 text-sm">
                <span className="w-12 text-xs text-muted-foreground">Rola</span>
                <select
                  aria-label="Rola nového účtu"
                  value={rola}
                  disabled={jeAdmin}
                  onChange={(e) => setRola(e.target.value as Rola)}
                  className="h-8 flex-1 rounded-md border bg-background px-2 text-sm"
                >
                  {ROLY.map((r) => (
                    <option key={r.kod} value={r.kod}>
                      {r.nazov}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-[10px] text-muted-foreground">
                {jeAdmin ? "Administrátor upravuje všetko." : POPIS_ROLY[rola]}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOtvoreny(false);
                setHeslo("");
              }}
            >
              Zrušiť
            </Button>
            <Button
              size="sm"
              disabled={!mozeUlozit || create.isPending}
              onClick={() => void uloz()}
              title={
                mozeUlozit
                  ? "Vytvoriť účet"
                  : `Vyplň prihlasovacie meno, meno a heslo (min. ${MIN_DLZKA_HESLA} znakov)`
              }
            >
              <UserPlus className="h-4 w-4" />
              {create.isPending ? "Vytváram…" : "Vytvoriť účet"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setOtvoreny(true);
            // Predvyplnené heslo — bezpečnejšie než to, čo admin vymyslí,
            // a prepísať sa dá
            setHeslo(vygenerujHeslo());
          }}
        >
          <UserPlus className="h-4 w-4" />
          Pridať účet
        </Button>
      )}
    </div>
  );
}

function UserRow({
  user,
  jaSam,
}: {
  user: AppUser;
  jaSam: boolean;
}): JSX.Element {
  const update = useUpdateUser();
  const [noveHeslo, setNoveHeslo] = useState("");
  const [resetOtvoreny, setResetOtvoreny] = useState(false);

  async function zmenRolu(rola: Rola): Promise<void> {
    try {
      await update.mutateAsync({ id: user.id, data: { rola } });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Rola sa nezmenila",
        description: extractErrorMessage(e as AxiosError),
      });
    }
  }

  async function prepni(pole: "je_admin" | "je_aktivny"): Promise<void> {
    try {
      await update.mutateAsync({ id: user.id, data: { [pole]: !user[pole] } });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Zmena neprešla",
        description: extractErrorMessage(e as AxiosError),
      });
    }
  }

  async function resetniHeslo(): Promise<void> {
    try {
      await update.mutateAsync({
        id: user.id,
        data: { nove_heslo: noveHeslo },
      });
      toast({
        title: `Heslo pre ${user.username} zmenené`,
        description: "Používateľ bol odhlásený zo všetkých prehliadačov.",
      });
      setNoveHeslo("");
      setResetOtvoreny(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Heslo sa nezmenilo",
        description: extractErrorMessage(e as AxiosError),
      });
    }
  }

  return (
    <>
      <TableRow className={user.je_aktivny ? undefined : "opacity-60"}>
        <TableCell className="font-mono text-sm">{user.username}</TableCell>
        <TableCell className="text-sm">
          {user.meno}
          {jaSam ? (
            <span className="ml-2 text-[10px] text-muted-foreground">(ty)</span>
          ) : null}
        </TableCell>
        <TableCell>
          {/* Rola (KOLO 231): admin všetko, inak rozpočtár alebo obchodník */}
          {user.je_admin ? (
            <Badge variant="default">Administrátor</Badge>
          ) : (
            <select
              aria-label={`Rola ${user.username}`}
              value={user.rola ?? "rozpoctar"}
              disabled={update.isPending}
              onChange={(e) => void zmenRolu(e.target.value as Rola)}
              title={POPIS_ROLY[user.rola ?? "rozpoctar"]}
              className="h-7 rounded-md border bg-background px-1.5 text-xs"
            >
              {ROLY.map((r) => (
                <option key={r.kod} value={r.kod}>
                  {r.nazov}
                </option>
              ))}
            </select>
          )}
        </TableCell>
        <TableCell>
          <span
            className={
              user.je_aktivny
                ? "text-sm text-emerald-700 dark:text-emerald-400"
                : "text-sm text-muted-foreground"
            }
            title={
              user.je_aktivny
                ? undefined
                : "Deaktivovaný sa nevie prihlásiť (účet zostáva v histórii)"
            }
          >
            {user.je_aktivny ? "Aktívny" : "Neaktívny"}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Úpravy účtu ${user.username}`}
                disabled={update.isPending}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => setResetOtvoreny(true)}
                title="Nastaviť nové heslo (staré netreba)"
              >
                <KeyRound className="h-4 w-4" />
                Zmeniť heslo
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* Vlastné práva si zobrať nemôže a sám seba nedeaktivuje —
                  backend to odmieta a bolo by to jediné kliknutie
                  k zamknutiu firmy z admin sekcie */}
              <DropdownMenuItem
                disabled={jaSam}
                onSelect={() => void prepni("je_admin")}
                title={
                  jaSam
                    ? "Vlastné admin práva si zobrať nemôžeš"
                    : "Prepnúť admin práva"
                }
              >
                {user.je_admin
                  ? "Odobrať admin práva"
                  : "Spraviť administrátorom"}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={jaSam}
                onSelect={() => void prepni("je_aktivny")}
                title={
                  jaSam
                    ? "Sám seba deaktivovať nemôžeš"
                    : "Deaktivovaný sa nevie prihlásiť (účet zostáva v histórii)"
                }
              >
                {user.je_aktivny ? "Deaktivovať" : "Aktivovať"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
      {resetOtvoreny ? (
        <TableRow>
          <TableCell colSpan={5} className="bg-background-alt/40">
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-80">
                <HesloPole
                  id={`heslo-${user.id}`}
                  label={`Nové heslo pre ${user.username}`}
                  hodnota={noveHeslo}
                  onZmena={setNoveHeslo}
                />
              </div>
              <Button
                size="sm"
                disabled={
                  noveHeslo.length < MIN_DLZKA_HESLA || update.isPending
                }
                onClick={() => void resetniHeslo()}
                title={
                  noveHeslo.length < MIN_DLZKA_HESLA
                    ? `Heslo musí mať aspoň ${MIN_DLZKA_HESLA} znakov`
                    : "Uložiť nové heslo"
                }
              >
                Uložiť heslo
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setResetOtvoreny(false);
                  setNoveHeslo("");
                }}
              >
                Zrušiť
              </Button>
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
